import { useEffect } from "react";

import { ApiError, apiFetch } from "@pontimed/api-client";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Owner side of "Pause services" (`_docs/service-pause.md`): read the state of
 * one workspace entity, pause it, restart it. Contract:
 * `apps/api/src/routes/account-service-pause.ts`.
 */

export type ServicePauseEntityType = "provider" | "facility" | "business";
export type ServicePauseDuration = "1w" | "1m" | "3m" | "date" | "indefinite";

export interface ServicePauseEntity {
  entityType: ServicePauseEntityType;
  entityId: string;
}

export interface ServicePauseSubscription {
  id: string;
  planTier: string;
  billing: "stripe" | "wallet";
  status: string;
  /** ISO — paid until, right now. */
  paidUntil: string | null;
  /** Paid days carried over to the restart (floored); null when not applicable. */
  carriedDays: number | null;
}

export interface ServicePauseState extends ServicePauseEntity {
  paused: boolean;
  pausedAt: string | null;
  /** ISO; null while paused = until the owner restarts them. */
  pausedUntil: string | null;
  /** Slot-occupying appointments from now to `pausedUntil` (or the `until` asked). */
  upcomingAppointments: number;
  subscriptions: ServicePauseSubscription[];
}

export interface PauseServicesInput extends ServicePauseEntity {
  duration: ServicePauseDuration;
  /** "YYYY-MM-DD", only with `duration: "date"`. */
  until?: string;
}

const BASE_PATH = "/api/account/service-pause";
/** Prefix of every service-pause query — invalidating it refreshes card + banner. */
export const SERVICE_PAUSE_KEY_ROOT = ["api", BASE_PATH] as const;
const PAUSE_KEY_ROOT = SERVICE_PAUSE_KEY_ROOT;
const BILLING_KEY = ["api", "/api/account/billing"] as const;

export function servicePauseKey(entity: ServicePauseEntity, until?: string | null) {
  return [...PAUSE_KEY_ROOT, entity.entityType, entity.entityId, until ?? null] as const;
}

/**
 * The entity's pause state. `until` (ISO) previews a pause that has not
 * started: the API then counts the appointments up to that moment.
 * No access (staff without billing rights, 403/404) is not retried.
 */
export function useServicePause(
  entity: ServicePauseEntity | null,
  options: {
    until?: string | null;
    enabled?: boolean;
    /** Keep showing the previous answer while a new `until` loads (the preview). */
    keepPrevious?: boolean;
  } = {},
) {
  const { until = null, enabled = true, keepPrevious = false } = options;
  return useQuery({
    queryKey: entity ? servicePauseKey(entity, until) : [...PAUSE_KEY_ROOT, "none"],
    queryFn: () => {
      if (!entity) throw new Error("service-pause: no entity");
      const params = new URLSearchParams({
        entityType: entity.entityType,
        entityId: entity.entityId,
      });
      if (until) params.set("until", until);
      return apiFetch.get<ServicePauseState>(`${BASE_PATH}?${params.toString()}`);
    },
    enabled: enabled && !!entity,
    staleTime: 30_000,
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 403 || error.status === 404)) &&
      failureCount < 2,
  });
}

function useAfterChange() {
  const queryClient = useQueryClient();
  return (state: ServicePauseState) => {
    // The plain (no-preview) query is the one card + banner read.
    queryClient.setQueryData(servicePauseKey(state), state);
    void queryClient.invalidateQueries({ queryKey: PAUSE_KEY_ROOT });
    // Paused subscriptions change status / period end on the billing tab.
    void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
  };
}

/**
 * A refused pause / restart can still have changed something: PAUSE_PARTIAL
 * leaves the entity paused, a 409 means another tab got there first. Re-read
 * instead of trusting what the card shows.
 */
function useAfterError() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PAUSE_KEY_ROOT });
    void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
  };
}

export function usePauseServices() {
  const afterChange = useAfterChange();
  const afterError = useAfterError();
  return useMutation({
    mutationFn: (input: PauseServicesInput) =>
      apiFetch.post<ServicePauseState>(BASE_PATH, {
        entityType: input.entityType,
        entityId: input.entityId,
        duration: input.duration,
        ...(input.duration === "date" && input.until ? { until: input.until } : {}),
      }),
    onSuccess: afterChange,
    onError: afterError,
  });
}

export function useResumeServices() {
  const afterChange = useAfterChange();
  const afterError = useAfterError();
  return useMutation({
    mutationFn: (entity: ServicePauseEntity) =>
      apiFetch.post<ServicePauseState>(`${BASE_PATH}/resume`, {
        entityType: entity.entityType,
        entityId: entity.entityId,
      }),
    onSuccess: afterChange,
    onError: afterError,
  });
}

/**
 * Keeps the pause state fresh when it changes elsewhere — the workers' restart
 * job at the end date, or another tab. `useAccountEvents` re-emits every
 * account SSE event as `account-state-changed`; `services.paused` /
 * `services.resumed` refresh the queries above.
 */
export function useServicePauseLiveSync() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const onEvent = (e: Event) => {
      const type = (e as CustomEvent<{ type?: unknown }>).detail?.type;
      if (typeof type !== "string" || !type.startsWith("services.")) return;
      void queryClient.invalidateQueries({ queryKey: PAUSE_KEY_ROOT });
      void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    };
    window.addEventListener("account-state-changed", onEvent);
    return () => window.removeEventListener("account-state-changed", onEvent);
  }, [queryClient]);
}

/** "YYYY-MM-DD" of a local date. */
export function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Calendar-month add that clamps to the month's last day (31 Jan + 1m → 28/29 Feb). */
function addMonths(date: Date, months: number): Date {
  const out = new Date(date);
  const day = out.getDate();
  out.setDate(1);
  out.setMonth(out.getMonth() + months);
  const lastDay = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate();
  out.setDate(Math.min(day, lastDay));
  return out;
}

/**
 * Bounds of the "until a date" picker. The API accepts tomorrow … +12 months
 * in the entity's country time zone, which the browser does not know; one day
 * of margin on each side keeps every offered date valid there too (a doctor
 * abroad, or a pick made around midnight).
 */
export function pauseDateBounds(now: Date = new Date()): { min: string; max: string } {
  const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
  const max = addMonths(now, 12);
  max.setDate(max.getDate() - 1);
  return { min: toDateInputValue(min), max: toDateInputValue(max) };
}

/**
 * The end of a pause that would start now, as ISO — only to preview the
 * appointments in that window; the API computes the real end itself.
 * null = no end (until restarted) or an invalid date.
 */
export function previewPauseEnd(
  duration: ServicePauseDuration,
  dateValue: string,
  now: Date = new Date(),
): string | null {
  switch (duration) {
    case "1w":
      return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
    case "1m":
      return addMonths(now, 1).toISOString();
    case "3m":
      return addMonths(now, 3).toISOString();
    case "date": {
      if (!isValidPauseDate(dateValue, now)) return null;
      const [y, m, d] = dateValue.split("-").map(Number);
      return new Date(y, m - 1, d).toISOString();
    }
    case "indefinite":
      return null;
  }
}

/** The next local midnight at or after `iso`, as ISO — a day-sized cache key. */
export function ceilToLocalDay(iso: string): string {
  const date = new Date(iso);
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (start.getTime() === date.getTime()) return start.toISOString();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).toISOString();
}

/** A "YYYY-MM-DD" inside the picker bounds. */
export function isValidPauseDate(value: string, now: Date = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const { min, max } = pauseDateBounds(now);
  // Same-format date strings compare lexicographically.
  return value >= min && value <= max;
}
